import type { Condition } from '@medplum/fhirtypes';

import { isPendingReview, isRefuted, isRetracted } from './review-markers';

/**
 * Page-side derivation of a patient's Conditions into the problem-list
 * buckets the card renders (ENG-5365 scope). Pure derivations over the
 * fetched set — no fetching, no React. The renderer owns single-resource
 * presentation; this module owns list membership and ordering (the walker
 * renders one view per resource, so bucketing is deliberately not a
 * registry view).
 */

export type ProblemList = {
  /** AI drafts awaiting review, newest first. */
  pending: Condition[];
  /** Current problems (clinicalStatus active/recurrence/relapse), newest first. */
  active: Condition[];
  /** Inactive / remission / resolved conditions, newest first. */
  past: Condition[];
};

/** Stable routing key for the drawer deep link — the resource id. Unlike
 *  observations (which group many resources per measure), one condition row
 *  is one resource, so the id is the natural unique token. */
export function conditionToken(condition: Condition): string {
  return condition.id ?? 'unknown';
}

/** The sortable clinical time of a condition (onset, then recorded). */
export function conditionSortKey(condition: Condition): string {
  return (
    condition.onsetDateTime ??
    condition.onsetPeriod?.start ??
    condition.recordedDate ??
    ''
  );
}

/** ICD-10(-CM) code on the condition, for the coded chip. */
export function icd10Code(condition: Condition): string | undefined {
  return condition.code?.coding?.find((coding) =>
    coding.system?.includes('icd-10'),
  )?.code;
}

const ACTIVE_CLINICAL_CODES = new Set(['active', 'recurrence', 'relapse']);

/**
 * Bucket a patient's conditions for the card. Retracted
 * (`entered-in-error`) conditions are dropped entirely — they are dismissed
 * drafts or superseded runs, not clinical history. Reviewed denials
 * (`refuted`, no longer pending) are also dropped: a correctly-captured
 * "denies X" is not a problem-list entry. A pending refuted draft stays in
 * `pending` so the reviewer can attest or dismiss the denial.
 */
export function buildProblemList(
  conditions: readonly Condition[],
): ProblemList {
  const newestFirst = (a: Condition, b: Condition) =>
    conditionSortKey(b).localeCompare(conditionSortKey(a));

  const pending: Condition[] = [];
  const active: Condition[] = [];
  const past: Condition[] = [];

  for (const condition of conditions) {
    if (isRetracted(condition)) continue;
    if (isPendingReview(condition)) {
      pending.push(condition);
      continue;
    }
    if (isRefuted(condition)) continue;
    const clinical = condition.clinicalStatus?.coding?.[0]?.code;
    // A confirmed condition without a clinical status is still a current
    // problem — never silently hide data over a missing code.
    if (!clinical || ACTIVE_CLINICAL_CODES.has(clinical)) {
      active.push(condition);
    } else {
      past.push(condition);
    }
  }

  return {
    pending: pending.sort(newestFirst),
    active: active.sort(newestFirst),
    past: past.sort(newestFirst),
  };
}

/** Find a condition by its routing token across every bucket. */
export function findConditionByToken(
  list: ProblemList,
  token: string | null,
): Condition | undefined {
  if (!token) return undefined;
  return [...list.pending, ...list.active, ...list.past].find(
    (condition) => conditionToken(condition) === token,
  );
}
